import { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { SignIn, Eye, EyeSlash, Warning } from '@phosphor-icons/react';
import { useAuth } from '../../contexts/AuthContext';

const formatApiErrorDetail = (detail) => {
    if (detail == null) return "Something went wrong. Please try again.";
    if (typeof detail === "string") return detail;
    if (Array.isArray(detail))
        return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
    if (detail && typeof detail.msg === "string") return detail.msg;
    return String(detail);
};

const AdminLogin = () => {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    
    const { login } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();

    const from = location.state?.from?.pathname || '/admin';

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setLoading(true);

        try {
            await login(email, password);
            navigate(from, { replace: true });
        } catch (err) {
            setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div 
            className="min-h-screen flex items-center justify-center bg-background-base px-4"
            data-testid="admin-login-page"
        >
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                className="w-full max-w-md"
            >
                {/* Logo */}
                <div className="text-center mb-8">
                    <img 
                        src="https://customer-assets.emergentagent.com/job_eng-software-hub/artifacts/3icn5ytz_Spindrift%20Engineering%20Software.png"
                        alt="Spindrift Engineering"
                        className="h-16 w-auto mx-auto mb-4"
                    />
                    <h1 className="text-2xl font-medium text-text-primary">
                        Admin Login
                    </h1>
                    <p className="text-text-muted text-sm mt-2">
                        Sign in to manage website content
                    </p>
                </div>

                {/* Login Form */}
                <form 
                    onSubmit={handleSubmit}
                    className="bg-background-surface border border-border-subtle rounded-sm p-6 sm:p-8 space-y-6"
                    data-testid="login-form"
                >
                    {error && (
                        <div 
                            className="flex items-center gap-2 p-3 bg-red-500/10 border border-red-500/30 rounded-sm text-red-400 text-sm"
                            data-testid="login-error"
                        >
                            <Warning size={18} />
                            {error}
                        </div>
                    )}

                    {/* Email */}
                    <div>
                        <label 
                            htmlFor="email" 
                            className="block text-sm font-medium text-text-secondary mb-2"
                        >
                            Email Address
                        </label>
                        <input
                            id="email"
                            type="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            className="form-input w-full px-4 py-3 rounded-sm text-text-primary"
                            placeholder="Enter your email"
                            required
                            autoComplete="off"
                            data-testid="login-email-input"
                        />
                    </div>

                    {/* Password */}
                    <div>
                        <label 
                            htmlFor="password" 
                            className="block text-sm font-medium text-text-secondary mb-2"
                        >
                            Password
                        </label>
                        <div className="relative">
                            <input
                                id="password"
                                type={showPassword ? 'text' : 'password'}
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                className="form-input w-full px-4 py-3 pr-12 rounded-sm text-text-primary"
                                placeholder="Enter your password"
                                required
                                autoComplete="new-password"
                                data-testid="login-password-input"
                            />
                            <button
                                type="button"
                                onClick={() => setShowPassword(!showPassword)}
                                className="absolute right-3 top-1/2 -translate-y-1/2 text-text-muted hover:text-text-primary transition-colors"
                                data-testid="toggle-password-visibility"
                            >
                                {showPassword ? <EyeSlash size={20} /> : <Eye size={20} />}
                            </button>
                        </div>
                    </div>

                    {/* Submit */}
                    <button
                        type="submit"
                        disabled={loading}
                        className="w-full btn-primary px-4 py-3 rounded-sm text-text-primary font-medium flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                        data-testid="login-submit-button"
                    >
                        {loading ? (
                            <span className="animate-pulse">Signing in...</span>
                        ) : (
                            <>
                                <SignIn size={20} />
                                Sign In
                            </>
                        )}
                    </button>
                </form>

                {/* Back to site */}
                <div className="text-center mt-6">
                    <a 
                        href="/"
                        className="text-text-muted hover:text-text-primary text-sm transition-colors"
                        data-testid="back-to-site-link"
                    >
                        ← Back to website
                    </a>
                </div>
            </motion.div>
        </div>
    );
};

export default AdminLogin;
